/**
 * Tâche 1443 (Eric 2026-10-02) — Historique des balayages.
 *
 * Choix d'Eric : « garder un historique des balayages pour comparer d'un mois
 * à l'autre ». L'écart par rapport au balayage PRÉCÉDENT est donc calculé et
 * affiché : un total seul ne dit pas si la situation s'améliore.
 */
import { CalendarBlank, SpinnerGap } from "@phosphor-icons/react";
import { useLang } from "../../contexts/LanguageContext";

export const HistoriqueBalayages = ({ balayages, libellesZone, chargement,
                                      onOuvrir, selectionne }) => {
  const { lang, tr } = useLang();

  const zoneLabel = (zone) => {
    const paire = libellesZone?.[zone];
    if (!paire) return zone;
    return lang === "en" ? paire[1] : paire[0];
  };

  const dateLisible = (iso) => {
    try {
      return new Date(iso).toLocaleString(lang === "en" ? "en-CA" : "fr-CA", {
        dateStyle: "medium", timeStyle: "short",
      });
    } catch {
      return iso;
    }
  };

  if (chargement) {
    return (
      <div className="flex items-center gap-2 text-xs text-zinc-500 py-6"
           data-testid="orthographe-historique-chargement">
        <SpinnerGap size={14} className="animate-spin" />
        {tr("Chargement de l'historique…", "Loading history…")}
      </div>
    );
  }

  if (!balayages.length) {
    return (
      <p className="text-xs text-zinc-400 py-6" data-testid="orthographe-historique-vide">
        {tr("Aucun balayage enregistré. Lancez-en un depuis l'onglet « Détection ».",
            "No scan recorded yet. Run one from the \"Detection\" tab.")}
      </p>
    );
  }

  return (
    <div className="space-y-2" data-testid="orthographe-historique">
      {balayages.map((b, i) => {
        const precedent = balayages[i + 1];
        const ecart = precedent ? b.nb_occurrences - precedent.nb_occurrences : null;
        return (
          <button
            key={b.id}
            type="button"
            onClick={() => onOuvrir(b.id)}
            className={`w-full text-left border rounded-xl px-4 py-3 transition-colors ${
              selectionne === b.id
                ? "border-[#002FA7] bg-blue-50"
                : "border-zinc-200 bg-white hover:bg-zinc-50"
            }`}
            data-testid={`orthographe-historique-${b.id}`}
          >
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="inline-flex items-center gap-2 text-xs text-zinc-700">
                <CalendarBlank size={14} className="text-zinc-400" />
                {dateLisible(b.lance_le)}
                <span className="text-zinc-400">· {b.lance_par_email}</span>
              </span>
              <span className="flex items-center gap-2 text-xs">
                <span className="font-semibold text-zinc-900">
                  {b.nb_occurrences} {tr("occurrence(s)",
                    b.nb_occurrences === 1 ? "occurrence" : "occurrences")}
                </span>
                {ecart != null && ecart !== 0 && (
                  <span className={ecart < 0 ? "text-emerald-700" : "text-rose-700"}>
                    {ecart < 0 ? "▼" : "▲"} {Math.abs(ecart)}
                  </span>
                )}
                {ecart === 0 && (
                  <span className="text-zinc-400">{tr("inchangé", "unchanged")}</span>
                )}
              </span>
            </div>
            <div className="flex flex-wrap gap-1.5 mt-2">
              {Object.entries(b.par_zone || {}).map(([zone, n]) => (
                <span key={zone}
                      className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-600 border border-zinc-200">
                  {zoneLabel(zone)} : {n}
                </span>
              ))}
              {b.tronque && (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-800 border border-amber-200">
                  {tr("tronqué", "truncated")}
                </span>
              )}
            </div>
          </button>
        );
      })}
    </div>
  );
};

export default HistoriqueBalayages;
